/**
 * Your life, laid against your dasha periods.
 *
 * This is the thing a Western horoscope app structurally cannot show you.
 * Vimshottari divides a life into long planetary chapters — a Venus mahadasha
 * runs twenty years, a Sun one runs six — and the claim the tradition makes is
 * about the FLAVOUR of a chapter, not the events in it. So the honest way to
 * present it is to put the events you logged yourself inside the chapters and
 * let you look. No scoring, no "see, it was accurate": just your own record
 * against the map.
 *
 * The vertical rail is drawn to scale, which is most of the point. A twenty
 * year Venus band physically dwarfs a six year Sun band, and that is the first
 * time most readers understand what a mahadasha actually is.
 */

import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  GRAHA_NAMES,
  type DashaPeriod,
  type GrahaBody,
  type JournalEntry,
  type VimshottariResult,
} from '@astrally/shared';
import { InfoDot } from '../../components/common';
import { Term } from '../../components/Term';

const MS_PER_YEAR = 365.25 * 86400000;
/** Rail scale. 20y Venus → 160px, 6y Sun → 48px, which reads at a glance. */
const PX_PER_YEAR = 8;
const MIN_BAND = 54;
const MAX_BAND = 240;
/** Beyond this, a band lists a count instead of every entry. */
const MAX_EVENTS_PER_BAND = 6;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const yearOf = (iso: string) => iso.slice(0, 4);
const dayOf = (iso: string) => iso.slice(0, 10);

/**
 * A dasha lord arrives as a planet id ('venus'); the reader gets the graha's
 * own name ('Shukra') — this app speaks Indian-first, and a dasha is as Jyotish
 * as a surface gets. Vimshottari lords are always the nine grahas, so the
 * `cap` fallback only guards an engine change.
 */
const lordName = (lord: string): string =>
  lord in GRAHA_NAMES ? GRAHA_NAMES[lord as GrahaBody] : cap(lord);

/** Dot colour by entry kind. Tokens only — never a raw colour literal. */
const KIND_COLOR: Record<JournalEntry['kind'], string> = {
  'life-event': 'var(--gold)',
  'prediction-rated': 'var(--accent)',
  'reading-saved': 'var(--accent-strong)',
  note: 'var(--text-faint)',
};

const KIND_GLYPH: Record<JournalEntry['kind'], string> = {
  'life-event': '◆',
  'prediction-rated': '✓',
  'reading-saved': '☾',
  note: '·',
};

// ---------------------------------------------------------------------------
// Placement
// ---------------------------------------------------------------------------

function spans(p: DashaPeriod, ms: number): boolean {
  return ms >= Date.parse(p.startISO) && ms < Date.parse(p.endISO);
}

/**
 * The mahadasha and antardasha running at an instant.
 *
 * The antardasha is what makes this worth building: "Venus mahadasha" covers
 * two decades and says almost nothing about a particular year, but
 * "Venus–Saturn" is a specific eighteen-month chapter.
 */
export function periodsAt(
  vim: VimshottariResult,
  ms: number,
): { maha?: DashaPeriod; antar?: DashaPeriod } {
  const maha = vim.periods.find((p) => spans(p, ms));
  const antar = maha?.children?.find((c) => spans(c, ms));
  return { maha, antar };
}

/** "Shukra–Shani", or just "Shukra" when the sub-period is not computed. */
export function dashaLabelAt(vim: VimshottariResult, ms: number): string | null {
  const { maha, antar } = periodsAt(vim, ms);
  if (!maha) return null;
  return antar ? `${lordName(maha.lord)}–${lordName(antar.lord)}` : lordName(maha.lord);
}

interface Placed {
  entry: JournalEntry;
  ms: number;
  /** 0..1 position inside its mahadasha. */
  fraction: number;
  antarLord?: string;
}

interface Band {
  period: DashaPeriod;
  startMs: number;
  endMs: number;
  years: number;
  height: number;
  active: boolean;
  placed: Placed[];
}

function buildBands(vim: VimshottariResult, entries: JournalEntry[], nowMs: number): Band[] {
  const bands: Band[] = vim.periods.map((period) => {
    const startMs = Date.parse(period.startISO);
    const endMs = Date.parse(period.endISO);
    const years = (endMs - startMs) / MS_PER_YEAR;
    return {
      period,
      startMs,
      endMs,
      years,
      height: Math.min(MAX_BAND, Math.max(MIN_BAND, Math.round(years * PX_PER_YEAR))),
      active: nowMs >= startMs && nowMs < endMs,
      placed: [],
    };
  });

  for (const entry of entries) {
    const ms = Date.parse(entry.atISO);
    if (Number.isNaN(ms)) continue;
    const band = bands.find((b) => ms >= b.startMs && ms < b.endMs);
    if (!band) continue; // outside the computed dasha span — counted separately
    band.placed.push({
      entry,
      ms,
      fraction: (ms - band.startMs) / (band.endMs - band.startMs),
      antarLord: band.period.children?.find((c) => spans(c, ms))?.lord,
    });
  }
  for (const band of bands) band.placed.sort((a, b) => b.ms - a.ms);

  // Trim the empty tail: nobody needs the four mahadashas after the one they
  // are living in, and a 120-year rail buries the part that has anything on it.
  let last = bands.length - 1;
  while (last > 0 && !bands[last].active && bands[last].placed.length === 0) last -= 1;
  const nowIndex = bands.findIndex((b) => b.active);
  return bands.slice(0, Math.max(last, nowIndex) + 1);
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export function TimelineCard({
  entries,
  vimshottari,
}: {
  entries: JournalEntry[];
  /** Absent when the birth time is too uncertain for a Moon nakshatra. */
  vimshottari?: VimshottariResult;
}) {
  const nowMs = Date.now();
  const dated = useMemo(
    () => entries.filter((e) => !Number.isNaN(Date.parse(e.atISO))),
    [entries],
  );
  const bands = useMemo(
    () => (vimshottari ? buildBands(vimshottari, dated, nowMs) : []),
    [vimshottari, dated, nowMs],
  );

  if (!vimshottari) return <NoDashaFallback entries={dated} />;
  if (dated.length === 0) return <TimelineEmptyState vim={vimshottari} nowMs={nowMs} />;

  const placedCount = bands.reduce((n, b) => n + b.placed.length, 0);
  const outside = dated.length - placedCount;

  return (
    <div className="card" data-mode="vedic">
      <div className="spread" style={{ alignItems: 'flex-start', marginBottom: 4 }}>
        <div>
          <div className="h3" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            Your life against your dashas
            <InfoDot term="vimshottari" />
          </div>
          <div className="tiny faint" style={{ marginTop: 2 }}>
            Chandra in {vimshottari.moonNakshatra.name} · {placedCount} of {dated.length} entries placed
          </div>
        </div>
        <span className="mode-chip">Jyotish</span>
      </div>

      <div className="tiny muted" style={{ margin: '8px 0 14px', lineHeight: 1.55 }}>
        Each band is one <strong>mahadasha</strong> — a chapter of your life ruled by one{' '}
        <Term id="graha">graha</Term>, drawn to scale, so a twenty-year band really is three times a
        six-year one. Your entries sit where they actually fell. What that <em>means</em> is yours
        to decide.
      </div>

      <div>
        {bands.map((band) => (
          <BandRow key={band.period.startISO} band={band} nowMs={nowMs} />
        ))}
      </div>

      {outside > 0 && (
        <div className="tiny faint" style={{ marginTop: 10, lineHeight: 1.5 }}>
          {outside} {outside === 1 ? 'entry falls' : 'entries fall'} outside the computed dasha span
          and {outside === 1 ? 'is' : 'are'} not drawn above — check the date if that looks wrong.
        </div>
      )}
    </div>
  );
}

function BandRow({ band, nowMs }: { band: Band; nowMs: number }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? band.placed : band.placed.slice(0, MAX_EVENTS_PER_BAND);
  const hidden = band.placed.length - visible.length;
  const nowFraction = band.active ? (nowMs - band.startMs) / (band.endMs - band.startMs) : null;

  const railStyle: CSSProperties = {
    position: 'relative',
    width: 10,
    flexShrink: 0,
    alignSelf: 'stretch',
    // The band height is a MINIMUM. Content taller than its time-share pushes
    // the band out, which costs a little cross-band proportionality — but dots
    // are placed as a percentage of the rail, so position WITHIN a chapter
    // stays exact either way.
    minHeight: band.height,
  };

  return (
    <div className="row" style={{ alignItems: 'stretch', gap: 12, minHeight: band.height }}>
      <div style={railStyle}>
        <div
          style={{
            position: 'absolute',
            left: 4,
            top: 0,
            bottom: 0,
            width: 2,
            borderRadius: 2,
            background: band.active ? 'var(--mode)' : 'var(--border-strong)',
            opacity: band.active ? 0.9 : 1,
          }}
        />
        {nowFraction !== null && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              width: 10,
              height: 2,
              top: `${nowFraction * 100}%`,
              background: 'var(--ok)',
            }}
          />
        )}
        {band.placed.map((p) => (
          <span
            key={p.entry.id}
            aria-hidden
            title={p.entry.title}
            style={{
              position: 'absolute',
              left: 1,
              top: `calc(${p.fraction * 100}% - 4px)`,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: KIND_COLOR[p.entry.kind],
              border: '1px solid var(--bg)',
            }}
          />
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0, paddingBottom: 16 }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span
            className="h3"
            style={{ color: band.active ? 'var(--mode-ink)' : 'var(--text)' }}
          >
            <Term id={`graha.${lordName(band.period.lord).toLowerCase()}`}>
              {lordName(band.period.lord)}
            </Term>
          </span>
          <span className="tiny faint">
            {yearOf(band.period.startISO)}–{yearOf(band.period.endISO)} ·{' '}
            {band.years.toFixed(band.years < 10 ? 1 : 0)}y
          </span>
          {band.active && (
            <span className="tiny" style={{ color: 'var(--ok)' }}>
              ● you are here
            </span>
          )}
        </div>

        {band.placed.length === 0 ? (
          <div className="tiny faint" style={{ marginTop: 4 }}>
            Nothing logged in this chapter.
          </div>
        ) : (
          <div className="col" style={{ gap: 6, marginTop: 6 }}>
            {visible.map((p) => (
              <div key={p.entry.id} className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
                <span
                  aria-hidden
                  style={{ color: KIND_COLOR[p.entry.kind], fontSize: '0.7rem', lineHeight: 1.6 }}
                >
                  {KIND_GLYPH[p.entry.kind]}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div
                    className="small"
                    style={{ overflowWrap: 'anywhere', lineHeight: 1.4 }}
                  >
                    {p.entry.title}
                  </div>
                  <div className="tiny faint">
                    {dayOf(p.entry.atISO)}
                    {p.antarLord && (
                      <>
                        {' · '}
                        <span style={{ color: 'var(--mode-ink)' }}>
                          {lordName(band.period.lord)}–{lordName(p.antarLord)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {hidden > 0 && (
              <button
                className="chip"
                style={{ minHeight: 32, alignSelf: 'flex-start' }}
                onClick={() => setExpanded(true)}
              >
                {hidden} more in this chapter
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty and fallback states — both sell the feature rather than apologise
// ---------------------------------------------------------------------------

function TimelineEmptyState({ vim, nowMs }: { vim: VimshottariResult; nowMs: number }) {
  const label = dashaLabelAt(vim, nowMs);
  const { maha, antar } = periodsAt(vim, nowMs);
  return (
    <div className="card col" data-mode="vedic" style={{ gap: 12 }}>
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <div className="h3" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          Your life against your dashas
          <InfoDot term="vimshottari" />
        </div>
        <span className="mode-chip">Jyotish</span>
      </div>

      {label && maha && (
        <div
          className="banner"
          style={{
            background: 'var(--mode-soft)',
            border: '1px solid var(--mode-line)',
            color: 'var(--mode-ink)',
            lineHeight: 1.55,
          }}
        >
          Right now you are in <strong>{label}</strong> — the {lordName(maha.lord)} chapter that
          runs {yearOf(maha.startISO)}–{yearOf(maha.endISO)}
          {antar
            ? `, and inside it the ${lordName(antar.lord)} sub-period until ${dayOf(antar.endISO)}`
            : ''}
          .
        </div>
      )}

      <div className="small muted" style={{ lineHeight: 1.6 }}>
        Vimshottari splits a life into long graha-ruled chapters. Log the things that actually
        happened to you — the move, the job, the break-up, the year everything changed — and they get
        pinned to the chapter that was running at the time.
      </div>
      <div className="small muted" style={{ lineHeight: 1.6 }}>
        You need about five real events before a shape appears. Backdate as far as you like; the
        date is the only thing that has to be roughly right.
      </div>
      <div className="tiny faint" style={{ lineHeight: 1.5 }}>
        Bonus: dated life events are exactly what the birth-time refiner needs, so a journal you kept
        anyway can sharpen your kundli.
      </div>
    </div>
  );
}

/**
 * No vimshottari — the birth time was too uncertain to fix a Moon nakshatra.
 * Rather than a blank card, this shows the plain chronology and names the one
 * thing that would unlock the overlay.
 */
function NoDashaFallback({ entries }: { entries: JournalEntry[] }) {
  const recent = [...entries]
    .sort((a, b) => Date.parse(b.atISO) - Date.parse(a.atISO))
    .slice(0, 8);
  return (
    <div className="card col" style={{ gap: 12 }}>
      <div className="h3" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        Your life against your dashas
        <InfoDot term="dasha" />
      </div>
      <div className="banner banner--gold" style={{ lineHeight: 1.55 }}>
        This overlay needs your <strong>birth time</strong>. Dasha periods are counted from
        Chandra’s exact nakshatra, and the Moon moves a full nakshatra in under a day — without a
        time, the chapters could be years out, so Astrally will not guess them.
      </div>
      <div className="small muted" style={{ lineHeight: 1.6 }}>
        Add or refine your birth time in your profile and this becomes a map of your life in
        dasha chapters. Everything you log in the meantime is kept and will be placed
        retroactively.
      </div>
      {recent.length > 0 && (
        <div className="col" style={{ gap: 6 }}>
          <div className="tiny faint">Meanwhile, your most recent entries:</div>
          {recent.map((e) => (
            <div key={e.id} className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
              <span
                aria-hidden
                style={{ color: KIND_COLOR[e.kind], fontSize: '0.7rem', lineHeight: 1.6 }}
              >
                {KIND_GLYPH[e.kind]}
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="small" style={{ overflowWrap: 'anywhere' }}>
                  {e.title}
                </div>
                <div className="tiny faint">{dayOf(e.atISO)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
